import axios from "axios";

axios.interceptors.request.use((config) => {
  const nonce = window.realIdApiSettings.nonce;
  config.headers["X-WP-Nonce"] = nonce;
  config.headers["Content-Type"] = "application/json";

  return config;
});

// Capture the rolling nonce WordPress emits in the X-WP-Nonce response header.
// The nonce baked into `realIdApiSettings` at page load drifts stale after WP's
// nonce tick (~12h) and starts returning 403 "Cookie check failed" on writes.
// Refreshing from each response keeps every subsequent request authenticated
// without making the merchant reload the order page.
function captureRollingNonce(headers) {
  if (!headers || !window.realIdApiSettings) return;
  const refreshed = headers["x-wp-nonce"] || headers["X-WP-Nonce"];
  if (refreshed) {
    window.realIdApiSettings.nonce = refreshed;
  }
}

axios.interceptors.response.use(
  (response) => {
    captureRollingNonce(response?.headers);

    // Detect WordPress REST responses that have been corrupted by another plugin
    // or theme echoing HTML during the request lifecycle (e.g., wp_resource_hints
    // dns-prefetch tags landing in the body before our JSON). axios can't parse
    // the mixed body so response.data ends up as a raw string. Reject so callers
    // can render the merchant-facing error screen instead of swallowing it.
    const url = response?.config?.url || "";
    const isRealIdRoute = url.includes("/real-id/v1/");
    if (
      isRealIdRoute &&
      typeof response.data === "string" &&
      /<[a-zA-Z!]/.test(response.data)
    ) {
      const error = new Error(
        "WordPress API returned a corrupted (non-JSON) response"
      );
      error.code = "CORRUPTED_API_RESPONSE";
      error.response = response;
      return Promise.reject(error);
    }

    return response;
  },
  (error) => {
    // WP sometimes still attaches a refreshed nonce on the failure path too —
    // grab it so the next attempt has a fighting chance.
    captureRollingNonce(error?.response?.headers);

    const status = error?.response?.status;
    const code = error?.response?.data?.code;
    const message = error?.response?.data?.message;
    if (
      status === 403 &&
      (code === "rest_cookie_invalid_nonce" ||
        message === "Cookie check failed")
    ) {
      error.isNonceError = true;
    }
    return Promise.reject(error);
  }
);

/**
 * https://github.com/axios/axios/blob/v1.x/lib/helpers/combineURLs.js
 * Creates a new URL by combining the specified URLs
 *
 * @param {string} baseURL The base URL
 * @param {string} relativeURL The relative URL
 *
 * @returns {string} The combined URL
 */
export function combineURLs(baseURL, relativeURL) {
  return relativeURL
    ? baseURL.replace(/\/?\/$/, "") + "/" + relativeURL.replace(/^\/+/, "")
    : baseURL;
}

/**
 * Get the root wp-json URL
 *
 * @returns String
 */
function root() {
  return window.realIdApiSettings.root;
}

/**
 * get the full WP API route for the given path
 *
 * @param {String} path
 * @returns
 */
function wpRoute(path) {
  return root()
    ? root().replace(/\/?\/$/, "") + "/" + path.replace(/^\/+/, "")
    : root();

  // combinedUrls not found? weird, moved the logic inside this method instead
  return combineUrls(root(), path);
}

export const getCurrentShop = () => {
  return axios.get(wpRoute(`/real-id/v1/shop`));
};

export const getChecks = (params) => {
  return axios.get(wpRoute("/real-id/v1/checks"), {
    params,
  });
};

export const createCheck = (data) => {
  return axios.post(wpRoute("/real-id/v1/checks"), data);
};

export const getCheck = (checkId) => {
  return axios.get(wpRoute(`/real-id/v1/checks/${checkId}`));
};

export const deleteCheckData = (checkId) => {
  return axios.post(wpRoute(`/real-id/v1/checks/${checkId}/data`));
};

export const deliverCheckReminder = (checkId, data) => {
  return axios.post(wpRoute(`/real-id/v1/checks/${checkId}/reminder`), data);
};

export const manuallyApproveCheck = (checkId, params) => {
  return axios.post(wpRoute(`/real-id/v1/checks/${checkId}/approve`), params);
};

export const manuallyRejectCheck = (checkId, params) => {
  return axios.post(wpRoute(`/real-id/v1/checks/${checkId}/reject`), params);
};

export const updateSettings = (settings) => {
  return axios.post(wpRoute(`/real-id/v1/shop/settings`), settings);
};

export const sendTestWebhook = ({ webhookUrl, webhookSecret }) => {
  return axios.post(wpRoute(`/real-id/v1/checks/send-test-webhook`), {
    webhookUrl,
    webhookSecret,
  });
};

export const deactivateLicense = () => {
  return axios.post(wpRoute("/real-id/v1/license/deactivate"));
};

export const activateLicense = ({ licenseKey }) => {
  return axios.post(wpRoute("/real-id/v1/license/activate"), {
    licenseKey,
  });
};

export const updateDeliveryMethods = (params) => {
  return axios.post(
    wpRoute("/real-id/v1/shop/update-delivery-methods"),
    params
  );
};

export const getConnectionStatus = ({ force = false } = {}) => {
  return axios.get(wpRoute("/real-id/v1/diagnostics/connection"), {
    params: force ? { force: 1 } : undefined,
  });
};

export const addEmailSignature = (params) => {
  return axios.post(wpRoute("/real-id/v1/shop/email-signature/add"), params);
};

export const checkEmailSignatureVerificationStatus = () => {
  return axios.post(
    wpRoute("/real-id/v1/shop/email-signature/check-verification-status")
  );
};

export const removeEmailSignature = (params) => {
  return axios.delete(
    wpRoute("/real-id/v1/shop/email-signature/remove"),
    params
  );
};

export const resetToSandboxMode = () => {
  return axios.post(wpRoute("/real-id/v1/shop/reset"));
};

export const listCategories = ({ currentPage = 1 }) => {
  return axios.get(wpRoute("/wc/v3/products/categories"), {
    params: {
      page: currentPage,
      per_page: 5,
    },
  });
};

export const getCurrentLicenseKey = () => {
  return axios.get(wpRoute("/real-id/v1/license-key"));
};

export const searchOrders = (params) => {
  return axios.get(wpRoute("/real-id/v1/orders/search"), {
    params: { ...params, ...(params.page ? { paged: params.page } : {}) },
    // wc uses a funny "paged" param name: https://github.com/woocommerce/woocommerce/wiki/wc_get_orders-and-WC_Order_Query#parameters
  });
};

export const searchCustomers = (params) => {
  return axios.get(wpRoute("/real-id/v1/customers/search"), { params });
};

export const getOrder = (id) => {
  return axios.get(wpRoute(`/real-id/v1/orders/${id}`));
};

export const checkEmailSignatureDKIMStatus = () => {
  return axios.post(
    wpRoute(`real-id/v1/shop/email-signature/check-dkim-status`)
  );
};

export const checkEmailSignatureReturnPathStatus = () => {
  return axios.post(
    wpRoute(`real-id/v1/shop/email-signature/check-return-path-status`)
  );
};

export const resetTransients = () => {
  return axios.delete(wpRoute(`real-id/v1/shop/transients`));
};

export const testShipStationConnection = (credentials) => {
  return axios.post(wpRoute("/real-id/v1/shipstation/test-connection"), credentials);
};

export const testSembleConnection = (credentials) => {
  return axios.post(wpRoute("/real-id/v1/semble/test-connection"), credentials);
};

export const syncShipStationOrder = (checkId) => {
  return axios.post(wpRoute("/real-id/v1/shipstation/sync-order"), { checkId });
};

export const getSubscriptionStatus = () => {
  return axios.get(wpRoute("/real-id/v1/shop/subscription-status"));
};

export const getStripePortalUrl = () => {
  return axios.post(wpRoute("/real-id/v1/shop/stripe/portal"));
};

export const addMultiSiteNetworkShop = (shopName) => {
  return axios.post(wpRoute("/real-id/v1/shop/multi-site-network/add"), { shopName });
};

export const removeMultiSiteNetworkShops = (shopNames) => {
  return axios.post(wpRoute("/real-id/v1/shop/multi-site-network/remove"), { shopNames });
};
